import http from "./http";
import { computeRefund } from "../utils/orderRefund";
import { API_URL, USE_MOCK } from "./config";
import { buildSummary, filterByStatus, findMockOrder, mockOrders, recalcOrder } from "./mock/orders";
import { getUserLocal } from "./auth";

// Order API - same endpoints the storefront (dm-commerce-b2b-web) uses:
//   GET   orders?page&limit&status[&paymentStatus&orderType]  -> payload: { data: [], meta: { totalPages, totalRecords } }
//   GET   orders/by-search?searchInput&status                  -> payload: { data: [] } (or [])
//   GET   orders/:id                                           -> payload: order
//   PATCH orders/:id  { id, status }                           -> e.g. status "Cancelled"
//   GET   orders/dashboard-summary                             -> payload: { counts, monthly }
const apiUrl = `${API_URL}orders`;

const delay = (value) => new Promise((resolve) => setTimeout(() => resolve(value), 250));

// -> { data: order[], meta: { total, totalPages } }
// the first payment of an order = the first PAYMENT row of its ledger (the API sends it as paymentDate)
const mockPaymentDate = (order) =>
  (order.ledgerData || [])
    .filter((entry) => entry.transactionCategory === "PAYMENT")
    .map((entry) => entry.createdDateTime)
    .sort()[0] || null;

// Read / unread and the last opened order, per logged in user (mock: kept in memory for the session)
const mockViews = {}; // orderId -> { isRead, lastOpenedAt }
const mockLastOpened = () =>
  Object.entries(mockViews)
    .filter(([, view]) => view.lastOpenedAt)
    .sort((a, b) => b[1].lastOpenedAt - a[1].lastOpenedAt)[0]?.[0];

// readFilter: "read" | "unread" (no value = all)
// sortBy: "paymentDate" | "orderDate" | "createdDateTime", sortOrder: "ASC" | "DESC" (no sortBy = newest first)
export async function getOrders({ page = 1, limit = 10, status, search, sortBy, sortOrder, readFilter } = {}) {
  if (USE_MOCK) {
    const lastOpened = mockLastOpened();
    let rows = filterByStatus(mockOrders, status).map((order) => ({
      ...order,
      paymentDate: mockPaymentDate(order),
      isRead: Boolean(mockViews[order.id]?.isRead),
      isLastOpened: String(order.id) === String(lastOpened),
      hasUnreadPublicNote: (order.notes || []).some((note) => note.privacy === "Public" && note.customerNotification && !note.customerNotification.isRead),
    }));
    if (readFilter === "read") rows = rows.filter((order) => order.isRead);
    if (readFilter === "unread") rows = rows.filter((order) => !order.isRead);
    if (sortBy) {
      const sign = String(sortOrder).toUpperCase() === "DESC" ? -1 : 1;
      rows = [...rows].sort((a, b) => {
        // orders that were never paid go last in both directions
        if (!a[sortBy] && !b[sortBy]) return 0;
        if (!a[sortBy]) return 1;
        if (!b[sortBy]) return -1;
        return (new Date(a[sortBy]) - new Date(b[sortBy])) * sign;
      });
    }
    if (search) {
      const needle = search.toLowerCase();
      rows = rows.filter((o) =>
        [o.code, o.productTitle, o.customerName, o.customerPhone, ...(o.shipment?.trackings || [])].some((v) =>
          String(v).toLowerCase().includes(needle)
        )
      );
    }
    const start = (page - 1) * limit;
    return delay({
      data: rows.slice(start, start + limit),
      meta: { total: rows.length, totalPages: Math.max(1, Math.ceil(rows.length / limit)) },
    });
  }

  const { data } = await http.get(apiUrl, {
    params: {
      page,
      limit,
      orderType: "Remote Sale",
      ...(status && { status }),
      ...(search && { searchString: search }),
      ...(sortBy && { sortBy, sortOrder: sortOrder || "ASC" }),
      ...(readFilter && { readFilter }),
    },
  });
  const { data: rows = [], meta = {} } = data.payload;
  return {
    data: rows,
    meta: {
      total: meta.totalRecords ?? meta.total ?? rows.length,
      totalPages: meta.totalPages ?? 1,
    },
  };
}

// The order's details page was opened: read, and the user's last opened order (shown highlighted in the list)
export async function markOrderOpened(id) {
  if (USE_MOCK) {
    mockViews[id] = { isRead: true, lastOpenedAt: Date.now() };
    return true;
  }

  await http.post(`${apiUrl}/${id}/opened`);
  return true;
}

// one or many orders marked read / unread by hand
export async function markOrdersRead(orderIds, isRead) {
  if (USE_MOCK) {
    orderIds.forEach((id) => {
      mockViews[id] = { ...mockViews[id], isRead };
    });
    return delay(true);
  }

  await http.patch(`${apiUrl}/views/read`, { orderIds, isRead });
  return true;
}

// orders the logged in user has not read yet (count on the Order menu)
export async function getUnreadOrderCount() {
  return (await getUnreadOrderCounts()).total;
}

// -> { total, byStatus: { "Pending Payment": 3, ... } }  unread orders of the logged in user, per status for the
// badge of each order list (a list's badge = the sum over its statuses)
export async function getUnreadOrderCounts() {
  if (USE_MOCK) {
    const unread = mockOrders.filter((order) => !mockViews[order.id]?.isRead);
    const byStatus = {};
    unread.forEach((order) => {
      byStatus[order.status] = (byStatus[order.status] || 0) + 1;
    });
    return { total: unread.filter((order) => order.status !== "Cancelled").length, byStatus };
  }

  const { data } = await http.get(`${apiUrl}/unread-count`);
  return { total: +data.payload?.unread || 0, byStatus: data.payload?.byStatus || {} };
}

/* ------------------------- invoice print + bulk delivery ------------------------- */
//   GET   orders/invoice-orders?b2bUserId&fromDate&toDate&status&searchString&page&limit  -> { data: [], meta }
//   POST  orders/invoice-data { orderIds }                 -> { data: [invoice], missing: [] }   many invoices, one request
//   PATCH orders/bulk/on-the-way-to-delivery { orderIds }  -> { updated, updatedOrders, skipped }  Processing For Delivery -> On The Way To Delivery

const totalOf = (order) => +order.state?.totalAmount || 0;

// a customer's orders for the invoice print page
export async function getInvoiceOrders({ b2bUserId, fromDate, toDate, status, searchString, page = 1, limit = 20 }) {
  if (USE_MOCK) {
    const needle = String(searchString || "").toLowerCase();
    const statuses = String(status || "").split(",").filter(Boolean);
    const rows = mockOrders
      .filter((order) => String(order.b2bUserId) === String(b2bUserId))
      .filter((order) => !statuses.length || statuses.includes(order.status))
      .filter((order) => !fromDate || String(order.orderDate).slice(0, 10) >= fromDate)
      .filter((order) => !toDate || String(order.orderDate).slice(0, 10) <= toDate)
      .filter((order) => !needle || [order.code, order.productTitle].some((value) => String(value).toLowerCase().includes(needle)))
      .map((order) => ({
        id: order.id,
        code: order.code,
        orderDate: order.orderDate,
        status: order.status,
        paymentStatus: order.paymentStatus,
        productTitle: order.productTitle,
        customerName: order.customerName,
        totalAmount: totalOf(order),
        paidAmount: +order.paidAmount || 0,
        itemCount: order.orderItems?.length || 0,
      }));
    return delay({
      data: rows.slice((page - 1) * limit, page * limit),
      meta: { total: rows.length, totalPages: Math.max(1, Math.ceil(rows.length / limit)) },
    });
  }

  const { data } = await http.get(`${apiUrl}/invoice-orders`, {
    params: { b2bUserId, page, limit, ...(fromDate && { fromDate }), ...(toDate && { toDate }), ...(status && { status }), ...(searchString && { searchString }) },
  });
  const { data: rows = [], meta = {} } = data.payload;
  return { data: rows, meta: { total: meta.totalRecords ?? rows.length, totalPages: meta.totalPages ?? 1 } };
}

const MAX_BULK = 200; // the API takes this many order ids at a time

// the invoices of many orders: ONE request per 200 orders, in the order the ids were given
export async function getInvoiceData(orderIds) {
  const ids = [...new Set(orderIds.map(Number))];
  if (USE_MOCK) {
    const byId = new Map(mockOrders.map((order) => [order.id, order]));
    return delay(ids.filter((id) => byId.has(id)).map((id) => ({ ...byId.get(id), items: byId.get(id).orderItems })));
  }

  const invoices = [];
  for (let i = 0; i < ids.length; i += MAX_BULK) {
    const { data } = await http.post(`${apiUrl}/invoice-data`, { orderIds: ids.slice(i, i + MAX_BULK) });
    invoices.push(...(data.payload?.data || []));
  }
  return invoices;
}

// Processing For Delivery -> On The Way To Delivery for the picked orders; orders in another status are skipped
// and reported back
export async function moveOrdersToOnTheWayToDelivery(orderIds) {
  const ids = [...new Set(orderIds.map(Number))];
  if (USE_MOCK) {
    const updatedOrders = [];
    const skipped = [];
    ids.forEach((id) => {
      const order = mockOrders.find((item) => item.id === id);
      if (!order) skipped.push({ id, code: null, status: null, reason: "Order not found" });
      else if (order.status !== "Processing For Delivery") skipped.push({ id, code: order.code, status: order.status, reason: `Status is "${order.status}", not "Processing For Delivery"` });
      else {
        order.status = "On The Way To Delivery";
        order.updatedDateTime = new Date().toISOString();
        updatedOrders.push({ id, code: order.code });
      }
    });
    if (!updatedOrders.length) throw new Error(`None of the selected orders is in "Processing For Delivery"`);
    return delay({ updated: updatedOrders.length, updatedOrders, skipped });
  }

  const { data } = await http.patch(`${apiUrl}/bulk/on-the-way-to-delivery`, { orderIds: ids });
  return data.payload;
}

/* ------------------- order list actions (tm RemoteSale tabs) ------------------- */
//   PATCH orders/pay-order-due/:code               { b2bUserId, payAmount, currency }
//   PATCH orders/pay-order-due-with-deposit/:code  { amount, transactionType, companyBankAccountId, ... }
//   POST  orders/create-local-shipping/:provider/:orderId

const settle = (order, amount) => {
  order.paidAmount = (+order.paidAmount || 0) + amount;
  const total = +order.state?.totalAmount || 0;
  order.paymentStatus = order.paidAmount >= total ? "PAID" : order.paidAmount > 0 ? "PARTIALLY_PAID" : "UNPAID";
};

const findByCode = (code) => mockOrders.find((o) => o.code === code);

export async function payOrderDue(code, body) {
  if (USE_MOCK) {
    const order = findByCode(code);
    if (!order) throw new Error("Order not found");
    settle(order, +body.payAmount || 0);
    if (order.status === "Pending Payment") order.status = "Order Initiated";
    return delay(true);
  }

  await http.patch(`${apiUrl}/pay-order-due/${code}`, body);
  return true;
}

// -> payload { ledger: { id }, bank?: { ledger: { id } } }
// Payments from the customer (Order Complete = one order, wallet Order Payment = many orders):
// the receipt travels in the same request, the API books wallet / bank / expense in one transaction.
//   bank credited = amount - charge, the charge goes to the Expense ledger (no charge = 0)
const toFormData = (body, file) => {
  const form = new FormData();
  Object.entries(body).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    form.append(key, Array.isArray(value) ? JSON.stringify(value) : value);
  });
  if (file) form.append("file", file);
  return form;
};

const mockOrderPayments = [];

// mock: same rules as the API (OrderPaymentService.pay)
const mockPay = (b2bUserId, orders, body, file) => {
  const amount = +body.amount || 0;
  const charge = +body.charge || 0;
  if (charge < 0) throw new Error("Charge cannot be negative");
  if (charge >= amount) throw new Error("Charge must be less than the total amount");
  const bankAmount = body.bankAmount === undefined || body.bankAmount === null ? amount - charge : +body.bankAmount;
  if (bankAmount < 0) throw new Error("Company bank amount cannot be negative");
  if (bankAmount > amount) throw new Error("Company bank amount cannot be more than the total amount");
  if (Math.abs(bankAmount + charge - amount) > 0.0001) throw new Error(`Company bank amount (${bankAmount}) + charge (${charge}) must equal the total amount (${amount})`);

  const dues = orders.map((o) => Math.max(0, (+o.state?.totalAmount || 0) - (+o.paidAmount || 0)));
  const totalDue = dues.reduce((sum, d) => sum + d, 0);
  if (orders.length > 1 && Math.abs(amount - totalDue) > 0.009) throw new Error(`Amount ${amount} must equal the total due of the selected orders (${totalDue})`);
  if (orders.length === 1 && amount > totalDue + 0.009) throw new Error(`Amount ${amount} is more than the due ${totalDue}`);

  const applied = orders.length > 1 ? dues : [amount];
  orders.forEach((order, i) => {
    order.paidAmount = (+order.paidAmount || 0) + applied[i];
    if (applied[i] >= dues[i] - 0.009) {
      order.status = "Completed";
      order.paymentStatus = "PAID";
    } else {
      order.paymentStatus = "PARTIALLY_PAID";
    }
  });

  const payment = {
    id: Date.now(),
    code: `OP${String(mockOrderPayments.length + 1).padStart(4, "0")}`,
    b2bUserId,
    totalAmount: amount,
    chargeAmount: charge,
    bankAmount,
    transactionType: body.transactionType,
    companyBankAccountId: body.companyBankAccountId,
    expenseHeadId: charge > 0 ? body.expenseHeadId : undefined,
    companyBankAccount: { bankName: `Bank A/c #${body.companyBankAccountId}` },
    bankTransId: body.bankTransId || "",
    remarks: body.remarks || "",
    scanCopyUrl: file ? URL.createObjectURL(file) : null,
    createdDateTime: body.createdDateTime || new Date().toISOString(),
    orders: orders.map((o, i) => ({ id: i + 1, orderId: o.id, orderCode: o.code, amount: applied[i] })),
  };
  mockOrderPayments.unshift(payment);
  return { payment, ledger: { id: Date.now() + 1 }, expense: charge > 0 ? { ledger: { id: Date.now() + 2 } } : null };
};

export async function payOrderDueWithDeposit(code, body, file) {
  if (USE_MOCK) {
    const order = findByCode(code);
    if (!order) throw new Error("Order not found");
    return delay(mockPay(body.b2bUserId, [order], body, file));
  }

  const { data } = await http.patch(`${apiUrl}/pay-order-due-with-deposit/${code}`, file ? toFormData(body, file) : body);
  return data.payload;
}

// the orders of a customer that still have a due amount
// -> [{ id, code, orderDate, status, paymentStatus, totalAmount, paidAmount, dueAmount }]
export async function getPayableOrders(b2bUserId) {
  if (USE_MOCK) {
    const rows = mockOrders
      .filter((o) => String(o.b2bUserId) === String(b2bUserId) && !["Cancelled", "Refunded"].includes(o.status))
      .map((o) => {
        const total = +o.state?.totalAmount || 0;
        const paid = +o.paidAmount || 0;
        return { id: o.id, code: o.code, orderDate: o.orderDate, status: o.status, paymentStatus: o.paymentStatus, currency: o.currency, totalAmount: total, paidAmount: paid, dueAmount: Math.max(0, total - paid) };
      })
      .filter((o) => o.dueAmount > 0);
    return delay(rows);
  }

  const { data } = await http.get(`${apiUrl}/payable-orders/${b2bUserId}`);
  return data.payload || [];
}

// POST orders/order-payments (multipart: the fields + the receipt as "file")
// body: { b2bUserId, orderIds, amount, charge, transactionType, companyBankAccountId, bankTransId, remarks, createdDateTime }
export async function payOrders(body, file) {
  if (USE_MOCK) {
    const orders = body.orderIds.map((id) => mockOrders.find((o) => o.id === id)).filter(Boolean);
    if (orders.length !== body.orderIds.length) throw new Error("Some orders were not found for this customer");
    return delay(mockPay(body.b2bUserId, orders, body, file));
  }

  const { data } = await http.post(`${apiUrl}/order-payments`, toFormData(body, file));
  return data.payload;
}

// payment history
// -> [{ code, createdDateTime, orders: [{ orderCode, amount }], totalAmount, chargeAmount, companyBankAccount, bankTransId, scanCopyUrl }]
export async function getOrderPayments(b2bUserId) {
  if (USE_MOCK) return delay(mockOrderPayments.filter((p) => String(p.b2bUserId) === String(b2bUserId)));

  const { data } = await http.get(`${apiUrl}/order-payments/${b2bUserId}`);
  return data.payload || [];
}

export async function createLocalShipping(provider, orderId) {
  if (USE_MOCK) return delay(true);

  await http.post(`${apiUrl}/create-local-shipping/${provider}/${orderId}`);
  return true;
}

export async function getOrder(id) {
  if (USE_MOCK) {
    const order = findMockOrder(id);
    if (!order) throw new Error("Order not found");
    return delay(JSON.parse(JSON.stringify(order)));
  }

  const { data } = await http.get(`${apiUrl}/${id}`);
  return data.payload;
}

export async function cancelOrder(id) {
  if (USE_MOCK) {
    const order = mockOrders.find((o) => o.id === id);
    if (order) order.status = "Cancelled";
    return delay(true);
  }

  await http.patch(`${apiUrl}/${id}`, { id, status: "Cancelled" });
  return true;
}

// -> { counts: { unpaid, paid, processed, completed }, monthly: [{ label, total }] }
export async function getOrderSummary() {
  if (USE_MOCK) return delay(buildSummary());

  const { data } = await http.get(`${apiUrl}/dashboard-summary`);
  return { counts: data.payload?.counts || {}, monthly: data.payload?.monthly || [] };
}

/* ----------------------- order details page actions ----------------------- */
// Same endpoints as tm-admin-web-react's ApiOrderDetails (services/inventory/*):
//   PATCH orders/:id                (status, partialAmount, purchase fields, discount/charges, delivery)
//   POST  orders/create-note        { badge, privacy, description, orderId }
//   DELETE orders/remove-note/:id
//   POST  order-shipments / PATCH order-shipments/:id   (shipment, weights, trackings)
//   PATCH order-items/:id           { price, quantity, productDiscount }

const mockUser = () => {
  const user = getUserLocal() || {};
  const [firstname = "Admin", ...rest] = String(user.name || "Admin").split(" ");
  return { identificationShortCode: "EMP-100", firstname, lastname: rest.join(" ") };
};

export async function updateOrder(id, body) {
  if (USE_MOCK) {
    const order = findMockOrder(id);
    if (!order) throw new Error("Order not found");

    const { purchaseInitiatorByUser, purchaseCompletedByUser, vendorId, vendorRate, ...rest } = body;
    Object.assign(order, rest, { updatedDateTime: new Date().toISOString() });
    if (vendorId) order.vendorId = vendorId; // the vendor picked in Initiate / Complete Purchase (the real API keeps it too)
    if (purchaseInitiatorByUser) order.purchaseInitiatorByUser = mockUser();
    if (purchaseCompletedByUser) order.purchaseCompletedByUser = mockUser();
    if ("partialAmount" in body || "orderDiscount" in body || "otherCharge" in body) recalcOrder(order);
    return delay(true);
  }

  await http.patch(`${apiUrl}/${id}`, body);
  return true;
}

export async function createOrderNote(body) {
  if (USE_MOCK) {
    const order = findMockOrder(body.orderId);
    order.notes = [
      ...(order.notes || []),
      {
        id: Date.now(),
        ...body,
        createdByUser: mockUser(),
        createdDateTime: new Date().toISOString(),
        // a PUBLIC note notifies the customer (unread until they open the order)
        ...(body.privacy === "Public" && { customerNotification: { isRead: false, readAt: null, sentAt: new Date().toISOString() } }),
      },
    ];
    return delay(true);
  }

  await http.post(`${apiUrl}/create-note`, body);
  return true;
}

export async function removeOrderNote(orderId, noteId) {
  if (USE_MOCK) {
    const order = findMockOrder(orderId);
    order.notes = (order.notes || []).filter((note) => note.id !== noteId);
    return delay(true);
  }

  await http.delete(`${apiUrl}/remove-note/${noteId}`);
  return true;
}

// tm: updateOrderShipment(shipment.id, body) when a shipment exists, else createOrderShipment({ ...body, orderId })
export async function saveOrderShipment(order, body) {
  if (USE_MOCK) {
    const record = findMockOrder(order.id);
    record.shipment = { ...(record.shipment || {}), ...body };
    delete record.shipment.orderId;
    return delay(true);
  }

  if (order.shipment?.id) await http.patch(`${API_URL}order-shipments/${order.shipment.id}`, body);
  else await http.post(`${API_URL}order-shipments`, { ...body, orderId: order.id });
  return true;
}

export async function updateOrderItem(orderId, itemId, body) {
  if (USE_MOCK) {
    const order = findMockOrder(orderId);
    const item = order.orderItems.find((entry) => entry.id === itemId);
    Object.assign(item, body);
    recalcOrder(order);
    return delay(true);
  }

  await http.patch(`${API_URL}order-items/${itemId}`, body);
  return true;
}

/* ------------------------- refunds (completed orders) ------------------------- */
//   POST orders/:id/refund-preview  { refundType: "FULL" | "ITEM", items: [{ orderItemId, quantity }], includeShipping, vendorRecoveredAmount }
//   POST orders/:id/refund          same body + { reason, remarks } -> payload { refund, ledger, order }
// The money goes back to the customer's wallet with a REFUND ledger row; the order keeps the refunded
// amount so its profit / loss stays exact.

// -> { lines, itemsAmount, taxAmount, discountAmount, shippingAmount, otherChargeAmount, totalAmount, finishesOrder, refundable }
export async function previewOrderRefund(id, body) {
  if (USE_MOCK) {
    const order = findMockOrder(id);
    if (!order) throw new Error("Order not found");
    return delay(computeRefund(order, body));
  }

  const { data } = await http.post(`${apiUrl}/${id}/refund-preview`, body);
  return data.payload;
}

export async function refundOrder(id, body) {
  if (USE_MOCK) {
    const order = findMockOrder(id);
    if (!order) throw new Error("Order not found");
    const result = computeRefund(order, body);

    const refund = {
      id: Date.now(),
      code: `RF${new Date().toISOString().slice(2, 4)}${new Date().toISOString().slice(5, 7)}${(order.refunds || []).length + 1}`,
      orderId: order.id,
      refundType: body.refundType,
      itemsAmount: result.itemsAmount,
      taxAmount: result.taxAmount,
      discountAmount: result.discountAmount,
      shippingAmount: result.shippingAmount,
      otherChargeAmount: result.otherChargeAmount,
      totalAmount: result.totalAmount,
      vendorRecoveredAmount: result.vendorRecoveredAmount,
      reason: body.reason || "",
      remarks: body.remarks || "",
      status: "COMPLETED",
      createdDateTime: new Date().toISOString(),
      createdByUser: { firstname: getUserLocal()?.name || "Admin" },
      items: result.lines.map((line, n) => ({ id: n + 1, ...line })),
    };

    result.lines.forEach((line) => {
      const item = order.orderItems.find((i) => i.id === line.orderItemId);
      item.refundedQuantity = (+item.refundedQuantity || 0) + line.quantity;
      item.refundedAmount = (+item.refundedAmount || 0) + line.amount;
    });
    order.refunds = [refund, ...(order.refunds || [])];
    order.refundedAmount = (+order.refundedAmount || 0) + result.totalAmount;
    order.refundedShippingAmount = (+order.refundedShippingAmount || 0) + result.shippingAmount;
    order.vendorRecoveredAmount = (+order.vendorRecoveredAmount || 0) + result.vendorRecoveredAmount;
    order.state = { ...order.state, refundedAmount: order.refundedAmount, netAmount: (+order.state?.totalAmount || 0) - order.refundedAmount };
    if (result.finishesOrder) {
      order.status = "Refunded";
      order.paymentStatus = "REFUNDED";
    }
    const ledger = {
      id: Date.now() + 1,
      referenceId: `RFD${refund.code}`,
      transactionCategory: "REFUND",
      transactionType: "REFUND",
      debit: 0,
      credit: result.totalAmount,
      createdDateTime: refund.createdDateTime,
      narration: `${order.code}-REFUND ${refund.code}`,
    };
    order.ledgerData = [...(order.ledgerData || []), ledger];
    return delay({ refund, ledger });
  }

  const { data } = await http.post(`${apiUrl}/${id}/refund`, body);
  return data.payload;
}
